/**
 * Bundle Module - Gestion des sélections (Taille, Offre, Couleurs avec quantités)
 * VERSION SIMPLIFIÉE - L'ajout au panier est géré par le bouton add-to-cart
 */

class BundleModule extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.blockId = this.dataset.blockId;

    // État actuel
    this.state = {
      selectedSize: null,
      selectedOffer: null,
      requiredQuantity: 1,
      colorQuantities: {},
      variants: []
    };
  }

  connectedCallback() {
    this.sizeInputs = this.querySelectorAll('.bundle-module__size-input');
    this.offerInputs = this.querySelectorAll('.bundle-module__offer-input');
    this.colorGrid = this.querySelector('[data-ref="color-grid"]');
    this.colorQuantityLabel = this.querySelector('[data-ref="color-quantity-label"]');
    this.colorWarning = this.querySelector('[data-ref="color-warning"]');
    this.quantityButtons = this.querySelectorAll('.bundle-module__quantity-btn');

    this.initializeDefaultState();
    this.bindEvents();
    this.loadProductData();
    this.updateQuantityLabel();
    setTimeout(() => this.updateAddToCartButton(), 100);
  }

  initializeDefaultState() {
    const checkedSize = this.querySelector('.bundle-module__size-input:checked');
    if (checkedSize) {
      this.state.selectedSize = checkedSize.value;
    }

    const checkedOffer = this.querySelector('.bundle-module__offer-input:checked');
    if (checkedOffer) {
      this.state.selectedOffer = parseInt(checkedOffer.id.match(/offer-(\d+)/)[1]);
      this.state.requiredQuantity = parseInt(checkedOffer.dataset.quantity);
      
      const selectedOfferLabel = this.querySelector('[data-ref="selected-offer"]');
      const offerLabel = checkedOffer.nextElementSibling;
      const offerTitleElement = offerLabel?.querySelector('.bundle-module__offer-title');
      if (selectedOfferLabel && offerTitleElement) {
        selectedOfferLabel.textContent = offerTitleElement.textContent.trim();
      }
    }

    const colorItems = this.querySelectorAll('.bundle-module__color-item');
    colorItems.forEach(item => {
      const color = item.dataset.color;
      this.state.colorQuantities[color] = 0;
    });
  }

  bindEvents() {
    this.sizeInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleSizeChange(e));
    });

    this.offerInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleOfferChange(e));
    });

    this.quantityButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleQuantityChange(e));
    });
  }

  handleSizeChange(e) {
    this.state.selectedSize = e.target.value;
    console.log('🎯 Taille sélectionnée:', this.state.selectedSize);

    const selectedSizeLabel = this.querySelector('[data-ref="selected-size"]');
    if (selectedSizeLabel) {
      const sizeLbl = this.querySelector('label[for="' + e.target.id + '"]');
      selectedSizeLabel.textContent = sizeLbl ? sizeLbl.textContent.trim() : this.state.selectedSize;
    }

    this.updateVariantImages();
    this.updateSelectedVariants();
  }

  handleOfferChange(e) {
    const input = e.target;
    this.state.selectedOffer = parseInt(input.id.match(/offer-(\d+)/)[1]);
    this.state.requiredQuantity = parseInt(input.dataset.quantity);

    console.log('🎯 Offre sélectionnée:', this.state.selectedOffer, 'Quantité requise:', this.state.requiredQuantity);

    const selectedOfferLabel = this.querySelector('[data-ref="selected-offer"]');
    const offerLabel = input.nextElementSibling;
    const offerTitleElement = offerLabel?.querySelector('.bundle-module__offer-title');
    if (selectedOfferLabel && offerTitleElement) {
      selectedOfferLabel.textContent = offerTitleElement.textContent.trim();
    }

    Object.keys(this.state.colorQuantities).forEach(color => {
      this.state.colorQuantities[color] = 0;
      const quantityInput = this.querySelector(`.bundle-module__quantity-input[data-color="${color}"]`);
      if (quantityInput) quantityInput.value = 0;
    });

    this.updateQuantityLabel();
    this.updateSelectedVariants();
  }

  handleQuantityChange(e) {
    const button = e.target.closest('.bundle-module__quantity-btn');
    if (!button) return;

    const action = button.dataset.action;
    const color = button.dataset.color;
    const input = this.querySelector(`.bundle-module__quantity-input[data-color="${color}"]`);

    let currentValue = parseInt(input.value) || 0;
    const currentTotal = this.getTotalQuantity();

    if (action === 'increase') {
      if (currentTotal < this.state.requiredQuantity) {
        currentValue++;
      }
    } else if (action === 'decrease' && currentValue > 0) {
      currentValue--;
    }

    input.value = currentValue;
    this.state.colorQuantities[color] = currentValue;

    console.log('🔢 Quantité mise à jour:', color, '=', currentValue);

    this.updateQuantityLabel();
    this.updateSelectedVariants();
  }

  getTotalQuantity() {
    return Object.values(this.state.colorQuantities).reduce((sum, qty) => sum + qty, 0);
  }

  updateQuantityLabel() {
    if (!this.colorQuantityLabel) return;

    const total = this.getTotalQuantity();
    const required = this.state.requiredQuantity;

    this.colorQuantityLabel.textContent = `(Total: ${total}/${required})`;

    if (this.colorWarning) {
      const requiredSpan = this.colorWarning.querySelector('[data-ref="required-quantity"]');
      if (requiredSpan) {
        requiredSpan.textContent = required;
      }

      if (total !== required) {
        this.colorWarning.style.display = 'block';
      } else {
        this.colorWarning.style.display = 'none';
      }
    }

    this.updateAddToCartButton();
  }

  updateAddToCartButton() {
    const total = this.getTotalQuantity();
    const required = this.state.requiredQuantity;

    const productForm = this.closest('product-form');
    if (!productForm) return;

    const addToCartButton = productForm.querySelector('[data-ref="add-to-cart-button"]');
    if (!addToCartButton) return;

    // CTA toujours actif : la validation se fait au clic (feedback shake + message)
    addToCartButton.disabled = false;
    addToCartButton.removeAttribute('aria-disabled');
  }

  async loadProductData() {
    try {
      const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
      if (!productHandle) return;

      const response = await fetch(`/products/${productHandle}.js`);
      const product = await response.json();

      this.product = product;
      console.log('📦 Produit chargé:', product.title);

      this.updateVariantImages();
    } catch (error) {
      console.error('❌ Erreur lors du chargement du produit:', error);
    }
  }

  updateVariantImages() {
    if (!this.product) return;

    const colorItems = this.querySelectorAll('.bundle-module__color-item');

    colorItems.forEach(item => {
      const color = item.dataset.color;
      let size = this.state.selectedSize;

      if (!size) {
        const firstSizeInput = this.querySelector('.bundle-module__size-input');
        if (firstSizeInput) {
          size = firstSizeInput.value;
        }
      }

      const imgElement = item.querySelector('.bundle-module__color-img');

      const variant = this.product.variants.find(v => {
        const options = v.options || [];
        return options.includes(size) && options.includes(color);
      });

      if (imgElement && variant && variant.featured_image) {
        imgElement.src = variant.featured_image.src;
        imgElement.alt = `${size} - ${color}`;
      } else if (imgElement && this.product.featured_image) {
        imgElement.src = this.product.featured_image;
        imgElement.alt = this.product.title;
      }
    });
  }

  updateSelectedVariants() {
    if (!this.product) return;

    const variants = [];

    Object.entries(this.state.colorQuantities).forEach(([color, quantity]) => {
      if (quantity > 0) {
        const size = this.state.selectedSize;

        const variant = this.product.variants.find(v => {
          const options = v.options || [];
          return options.includes(size) && options.includes(color);
        });

        if (variant) {
          variants.push({
            id: variant.id,
            quantity: quantity
          });
        }
      }
    });

    this.state.variants = variants;
    console.log('🎯 Variantes sélectionnées pour le panier:', variants);
  }

  getSelectedItems() {
    const total = this.getTotalQuantity();

    if (!this.state.selectedSize) {
      console.warn('⚠️ Aucune taille sélectionnée');
      this.showSizeError();
      return {
        items: [],
        total_price: 0,
        total_compare_at_price: 0,
        isValid: false,
        error: 'no_size'
      };
    }

    if (total !== this.state.requiredQuantity) {
      console.warn('⚠️ Quantité incorrecte:', total, 'au lieu de', this.state.requiredQuantity);
      return {
        items: [],
        total_price: 0,
        total_compare_at_price: 0,
        isValid: false,
        error: 'invalid_quantity'
      };
    }

    const selectedOfferInput = this.querySelector('.bundle-module__offer-input:checked');
    const offerPrice = selectedOfferInput ? parseInt(selectedOfferInput.dataset.price) : 0;
    const offerComparePrice = selectedOfferInput ? parseInt(selectedOfferInput.dataset.comparePrice || 0) : 0;

    console.log('💰 Prix de l\'offre:', offerPrice / 100, 'GBP');

    return {
      items: this.state.variants,
      total_price: offerPrice,
      total_compare_at_price: offerComparePrice,
      isValid: true
    };
  }

  showSizeError() {
    const sizeSection = this.querySelector('.bundle-module__size-section');
    if (!sizeSection) return;

    const sizeTitle = sizeSection.querySelector('.bundle-module__section-title');
    if (!sizeTitle) return;

    sizeTitle.classList.add('bundle-module__section-title--error');

    setTimeout(() => {
      sizeTitle.classList.remove('bundle-module__section-title--error');
    }, 3000);

    sizeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

if (!customElements.get('bundle-module')) {
  customElements.define('bundle-module', BundleModule);
}

// NOTE: L'interception du formulaire a été supprimée
// L'ajout au panier est maintenant géré directement par le bouton add-to-cart
console.log('✅ Bundle module chargé (sans interception du formulaire)');